import { useState, useCallback, useEffect, useMemo } from "react";
import { useTenant } from "../../../hooks/useTenant";
import { toast } from "react-toastify";
import MediaService from "../../../services/media_service";
import { mediaDefaults } from "../../../models/media";

/**
 * useMediaPage
 * Hook orquestrador para a página de Mídias.
 * Segue o padrão de useMembersPage: concentra toda a lógica de negócio.
 */
export const useMediaPage = () => {
    const { idTenant, user, loading: tenantLoading } = useTenant();

    // Estados de Dados
    const [medias, setMedias] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Estados do Formulário/Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState(mediaDefaults);
    const [isSaving, setIsSaving] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");

    /**
     * Carrega todas as mídias do tenant.
     */
    const loadMedias = useCallback(async () => {
        if (!idTenant) return;
        setIsInitialLoading(true);
        try {
            const data = await MediaService.getAll(idTenant);
            setMedias(data);
        } catch (error) {
            console.error("Erro ao carregar mídias:", error);
            toast.error("Erro ao carregar mídias.");
        } finally {
            setIsInitialLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            loadMedias();
        }
    }, [tenantLoading, idTenant, loadMedias]);

    /**
     * Filtra mídias por status.
     */
    const filteredMedias = useMemo(() => {
        if (statusFilter === "all") return medias;
        return medias.filter(m => m.status === statusFilter);
    }, [medias, statusFilter]);

    /**
     * Toggle Modal.
     */
    const handleToggleModal = useCallback(() => {
        if (isModalOpen) {
            setFormState(mediaDefaults);
        }
        setIsModalOpen(prev => !prev);
    }, [isModalOpen]);

    /**
     * Editar mídia — abre modal com dados preenchidos.
     */
    const handleEdit = useCallback((media) => {
        setFormState(media);
        setIsModalOpen(true);
    }, []);

    /**
     * Salvar mídia (criação ou atualização).
     * @param {Object} data - Dados do formulário
     * @param {File|null} imageFile - Arquivo de imagem (se novo upload, type=single)
     * @param {File[]|null} carouselFiles - Arquivos do carrossel (se type=carousel)
     */
    const handleSave = useCallback(async (data, imageFile = null, carouselFiles = null) => {
        setIsSaving(true);
        try {
            await MediaService.save(data, idTenant, user, imageFile, carouselFiles);
            toast.success(data.id ? "Mídia atualizada!" : "Mídia criada!");
            await loadMedias();
            handleToggleModal();
        } catch (error) {
            console.error("Erro ao salvar mídia:", error);
            toast.error("Erro ao salvar mídia.");
        } finally {
            setIsSaving(false);
        }
    }, [idTenant, user, loadMedias, handleToggleModal]);

    /**
     * Excluir mídia.
     */
    const handleDelete = useCallback(async (mediaId, mediaName) => {
        try {
            await MediaService.delete(mediaId, idTenant, user, mediaName);
            toast.success("Mídia excluída!");
            await loadMedias();
        } catch (error) {
            console.error("Erro ao excluir mídia:", error);
            toast.error("Erro ao excluir mídia.");
        }
    }, [idTenant, user, loadMedias]);

    return {
        // Dados
        medias: filteredMedias,
        isInitialLoading,
        statusFilter,
        setStatusFilter,

        // Modal/Form
        isModalOpen,
        handleToggleModal,
        formState,
        setFormState,
        isSaving,
        handleSave,
        handleEdit,
        handleDelete,

        // Status
        isReady: !tenantLoading && !!idTenant
    };
};
